import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { Student9n11Detail } from '../../../services/kvsResult9n11Api';

interface OutletCtx {
  student: Student9n11Detail;
  fullName: string;
  attendancePct: number;
  level: string;
}

const GRADE_THRESHOLDS = [
  { grade: 'A+', min: 90, color: '#059669' }, { grade: 'A', min: 75, color: '#10B981' },
  { grade: 'B+', min: 60, color: '#3B82F6' }, { grade: 'B', min: 45, color: '#F59E0B' },
  { grade: 'C', min: 33, color: '#F97316' }, { grade: 'F', min: 0, color: '#EF4444' },
];
const getGrade = (score: number) => GRADE_THRESHOLDS.find(g => score >= g.min) ?? GRADE_THRESHOLDS[GRADE_THRESHOLDS.length - 1];

const SummaryCard: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-2xl font-bold" style={{ color: color ?? '#111827' }}>{value}</p>
  </div>
);

const AcademicTab: React.FC = () => {
  const { student, attendancePct, level } = useOutletContext<OutletCtx>();
  const grade = getGrade(student.marks);
  const passMark = 33;
  const passGap = student.marks - passMark;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Marks" value={`${student.marks}%`} color={grade.color} />
        <SummaryCard label="Grade" value={grade.grade} color={grade.color} />
        <SummaryCard label="Attendance" value={`${attendancePct}%`} color={attendancePct >= 75 ? '#059669' : '#F59E0B'} />
        <SummaryCard label="Level" value={level} color={level === 'Excellent' ? '#059669' : level === 'Good' ? '#2563EB' : level === 'Average' ? '#F59E0B' : '#EF4444'} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Performance Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-500 mb-1">Marks</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${student.marks}%`, backgroundColor: grade.color }} /></div>
              <span className="text-sm font-semibold" style={{ color: grade.color }}>{student.marks}%</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Attendance</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${attendancePct}%`, backgroundColor: attendancePct >= 75 ? '#059669' : '#F59E0B' }} /></div>
              <span className="text-sm font-semibold">{attendancePct}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {passGap >= 0 ? <TrendingUp size={16} className="text-emerald-600" /> : <TrendingDown size={16} className="text-red-600" />}
            <div>
              <p className="text-xs text-gray-500">vs Pass Mark (33%)</p>
              <p className={`text-sm font-semibold ${passGap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{passGap >= 0 ? '+' : ''}{passGap.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Attendance Details</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><p className="text-xs text-gray-500">Working Days</p><p className="text-2xl font-bold text-gray-900">{student.workingDay}</p></div>
          <div><p className="text-xs text-gray-500">Present</p><p className="text-2xl font-bold text-emerald-600">{student.presentDay}</p></div>
          <div><p className="text-xs text-gray-500">Absent</p><p className="text-2xl font-bold text-red-500">{student.workingDay - student.presentDay}</p></div>
        </div>
      </div>
    </div>
  );
};

export default AcademicTab;
